import { useState, useCallback, useMemo, memo } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SignedIn, SignedOut, UserButton } from "@clerk/clerk-react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { useTranslation } from "react-i18next";
import type { Id } from "../../../convex/_generated/dataModel";
import { 
  MessageSquare, 
  Send, 
  Loader2, 
  ArrowLeft,
  Trash2,
  Check,
  CheckCheck,
  Search
} from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { Navbar } from "@/components/Navbar";

// Memoized conversation list item
const ConversationItem = memo(function ConversationItem({ 
  conv, 
  isSelected, 
  onSelect 
}: { 
  conv: any; 
  isSelected: boolean; 
  onSelect: (id: Id<"conversations">) => void;
}) {
  const handleClick = useCallback(() => {
    onSelect(conv._id);
  }, [conv._id, onSelect]);

  return (
    <button
      onClick={handleClick}
      className={`w-full p-4 text-left border-b border-border hover:bg-muted/50 transition-colors ${
        isSelected ? "bg-muted" : ""
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <span className="text-sm font-medium">
            {conv.otherParticipant?.name?.[0]?.toUpperCase() ?? "?"}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{conv.otherParticipant?.name ?? "Unknown"}</p>
          <p className="text-sm text-muted-foreground truncate">{conv.ad?.title}</p>
        </div>
        {conv.unreadCount > 0 && (
          <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
            {conv.unreadCount}
          </span>
        )}
      </div>
    </button>
  );
});

// Memoized message bubble
const MessageBubble = memo(function MessageBubble({ 
  message, 
  isOwn, 
  onDelete 
}: { 
  message: any; 
  isOwn: boolean; 
  onDelete?: (id: Id<"messages">) => void;
}) {
  const handleDelete = useCallback(() => {
    if (onDelete) onDelete(message._id);
  }, [message._id, onDelete]);

  return (
    <div className={`flex ${isOwn ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[70%] p-3 rounded-lg ${
        isOwn ? "bg-primary text-primary-foreground" : "bg-muted"
      }`}>
        <p className="text-sm">{message.content}</p>
        <div className="flex items-center justify-end gap-1 mt-1">
          <span className="text-xs opacity-70">
            {formatDistanceToNow(new Date(message._creationTime), { addSuffix: true })}
          </span>
          {isOwn && (
            <>
              {message.read ? <CheckCheck className="w-3 h-3" /> : <Check className="w-3 h-3" />}
              {onDelete && (
                <button onClick={handleDelete} className="ml-1 opacity-50 hover:opacity-100" title="Delete message">
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
});

export default function Messages() {
  const { t } = useTranslation();
  const [selectedConversation, setSelectedConversation] = useState<Id<"conversations"> | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Convex queries and mutations
  const conversations = useQuery(api.messages.getConversations);
  const conversationItems = (conversations ?? []) as any[];
  const messages = useQuery(
    api.messages.getMessages,
    selectedConversation ? { conversationId: selectedConversation.toString() } : "skip"
  );
  const messageItems = (messages ?? []) as any[];
  const sendMessageMutation = useMutation(api.messages.sendMessage);
  const markAsReadMutation = useMutation(api.messages.markAsRead);
  const deleteMessageMutation = useMutation(api.messages.deleteMessage);

  // Get current user to check message ownership
  const currentUser = useQuery(api.auth.getCurrentUser);

  // Memoize filtered conversations to avoid recalculating on every render
  const filteredConversations = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return conversationItems.filter((conv) =>
      conv.otherParticipant?.name?.toLowerCase().includes(query) ||
      conv.ad?.title?.toLowerCase().includes(query)
    );
  }, [conversationItems, searchQuery]);

  // Memoize selected conversation lookup
  const selectedConv = useMemo(() => 
    conversationItems.find((c) => c._id === selectedConversation),
    [conversationItems, selectedConversation]
  );

  // Stable callback for selecting conversations
  const handleSelectConversation = useCallback(async (conversationId: Id<"conversations">) => {
    setSelectedConversation(conversationId);
    try {
      await markAsReadMutation({ conversationId: conversationId.toString() });
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  }, [markAsReadMutation]);

  // Stable callback for sending messages
  const handleSendMessage = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedConversation || !newMessage.trim() || !selectedConv?.otherParticipant) return;

    try {
      await sendMessageMutation({
        receiverId: selectedConv.otherParticipant.id,
        content: newMessage.trim(),
        adId: selectedConv.ad?.id,
      });
      setNewMessage("");
    } catch (error) {
      toast.error(t("errors.networkError"));
    }
  }, [selectedConversation, newMessage, selectedConv, sendMessageMutation, t]);

  // Stable callback for deleting messages
  const handleDeleteMessage = useCallback(async (messageId: Id<"messages">) => {
    try {
      await deleteMessageMutation({ messageId });
      toast.success(t("messages.delete"));
    } catch (error) {
      toast.error(t("errors.deleteFailed"));
    }
  }, [deleteMessageMutation, t]);

  // Stable callback for search input
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  // Stable callback for message input
  const handleMessageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="container py-4">
        <div className="flex items-center gap-4 mb-6">
          <Link href="/dashboard">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t("common.back")}
            </Button>
          </Link>
          <h1 className="text-2xl font-bold">{t("messages.title")}</h1>
        </div>

        <SignedIn>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-180px)]">
            {/* Conversations List */}
            <div className="card-elegant overflow-hidden flex flex-col">
              <div className="p-4 border-b border-border">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder={t("common.search")}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {conversations === undefined ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : filteredConversations.length === 0 ? (
                  <div className="p-8 text-center">
                    <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">{t("messages.noMessages")}</p>
                    <p className="text-sm text-muted-foreground mt-2">{t("messages.startConversation")}</p>
                  </div>
                ) : (
                  filteredConversations.map((conv) => (
                    <ConversationItem
                      key={conv._id}
                      conv={conv}
                      isSelected={selectedConversation === conv._id}
                      onSelect={handleSelectConversation}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Message Thread */}
            <div className="lg:col-span-2 card-elegant overflow-hidden flex flex-col">
              {selectedConversation ? (
                <>
                  {/* Header */}
                  <div className="p-4 border-b border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold">
                        {selectedConv?.otherParticipant?.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <p className="font-semibold">{selectedConv?.otherParticipant?.name}</p>
                        {selectedConv?.ad && (
                          <Link href={`/ads/${selectedConv.ad.id}`}>
                            <a className="text-sm text-primary hover:underline">
                              {selectedConv.ad.title}
                            </a>
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages === undefined ? (
                      <div className="flex items-center justify-center h-32">
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      </div>
                    ) : messageItems.length === 0 ? (
                      <div className="text-center text-muted-foreground py-8">
                        {t("messages.noMessages")}
                      </div>
                    ) : (
                      messageItems.map((msg) => (
                        <MessageBubble
                          key={msg._id}
                          message={msg}
                          isOwn={currentUser?._id === msg.senderId}
                          onDelete={currentUser?._id === msg.senderId ? handleDeleteMessage : undefined}
                        />
                      ))
                    )}
                  </div>

                  {/* Message Input */}
                  <form onSubmit={handleSendMessage} className="p-4 border-t border-border">
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={handleMessageChange}
                        placeholder={t("messages.typeMessage")}
                        className="flex-1"
                      />
                      <Button type="submit" disabled={!newMessage.trim()}>
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">{t("messages.startConversation")}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </SignedIn>

        <SignedOut>
          <div className="text-center py-16">
            <MessageSquare className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t("auth.signInToPost")}</h2>
            <p className="text-muted-foreground mb-6">{t("messages.startConversation")}</p>
            <Link href="/sign-in">
              <Button size="lg">{t("nav.signIn")}</Button>
            </Link>
          </div>
        </SignedOut>
      </div>
    </div>
  );
}
